import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { IconButton, Tooltip } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import DragIndicatorIcon from '@material-ui/icons/DragIndicator';
import RestoreIcon from '@material-ui/icons/Restore';
import { useLocalizedMessage } from 'util/useLocalizedMessage';
import applyEditorTheme from './editorTheme';
import orderedCollectionStyles from './orderedCollectionStyles';
import AutoFillControl from './AutoFillControl';
import PinAction from './PinAction';
import useOrderedCollectionMotion from './useOrderedCollectionMotion';
import RowEditActions from './RowEditActions';

const MAX_VISIBLE_QUERIES = 10;
const normalizeTerm = (term) => String(term || '').trim().toLowerCase();

const themedStyles = applyEditorTheme({
  wrapper: {
    overflow: 'hidden',
    border: '1px solid #dee2e6',
    borderRadius: 6,
    marginBottom: 16,
  },
  header: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    padding: '12px 14px',
    borderBottom: '1px solid #dee2e6',
    background: '#f8f9fa',
  },
  headerTitle: { marginBottom: 2, fontSize: 14, fontWeight: 600 },
  headerSub: { color: '#6c757d', fontSize: 12 },
  headerMeta: {
    flexShrink: 0,
    paddingLeft: 8,
    color: '#868e96',
    fontSize: 11,
    textAlign: 'right',
  },
  orderToolbar: {
    display: 'flex',
    minHeight: 42,
    alignItems: 'center',
    justifyContent: 'flex-end',
    padding: '6px 14px',
    borderBottom: '1px solid #edf2f0',
    background: '#fbfefd',
  },
  resetOrderBtn: {
    display: 'inline-flex',
    minHeight: 32,
    alignItems: 'center',
    gap: 6,
    padding: '4px 10px',
    border: 0,
    borderRadius: 999,
    background: 'transparent',
    color: '#66708a',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: 12,
    fontWeight: 600,
  },
  resetIcon: { fontSize: 16 },
  zone: { background: '#fff' },
  queryRow: {
    display: 'grid',
    width: '100%',
    minHeight: 48,
    boxSizing: 'border-box',
    gridTemplateColumns: '28px minmax(0, 1fr) auto 32px',
    alignItems: 'center',
    gap: 8,
    padding: '7px 14px',
    borderBottom: '1px solid #edf2f0',
    background: '#fff',
    cursor: 'grab',
    transition: 'background-color 140ms ease, box-shadow 140ms ease',
  },
  queryText: {
    minWidth: 0,
    overflow: 'hidden',
    color: '#1e2248',
    cursor: 'text',
    fontSize: 13,
    lineHeight: 1.4,
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  generatedCount: {
    color: '#8790a5',
    fontSize: 11,
    fontVariantNumeric: 'tabular-nums',
    textAlign: 'right',
  },
  dragHandle: {
    display: 'flex',
    width: 28,
    height: 32,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 6,
    color: '#8790a5',
    cursor: 'grab',
    userSelect: 'none',
  },
  dragIcon: { fontSize: 19 },
  hideBtn: {
    width: 32,
    height: 32,
    padding: 0,
    color: '#8790a5',
    transition: 'opacity 140ms ease, color 140ms ease, background-color 140ms ease',
  },
  emptyState: {
    margin: '12px 16px',
    padding: '20px 16px',
    border: '1px dashed #c8d9d2',
    borderRadius: 6,
    background: '#fbfefd',
    color: '#66708a',
    fontSize: 13,
    lineHeight: 1.5,
    textAlign: 'center',
  },
  addRow: {
    display: 'flex',
    gap: 8,
    padding: '12px 14px',
    borderTop: '1px solid #edf2f0',
    background: '#fbfefd',
  },
  addInput: {
    flex: 1,
    minWidth: 0,
    minHeight: 40,
    padding: '8px 12px',
    border: '1px solid #d5e1dd',
    borderRadius: 8,
    color: '#1e2248',
    fontSize: 13,
  },
  addBtn: {
    minHeight: 40,
    padding: '0 14px',
    border: '1px solid var(--kea-color-primary-border)',
    borderRadius: 999,
    background: '#fff',
    color: 'var(--kea-color-primary)',
    cursor: 'pointer',
    fontSize: 12,
    fontWeight: 600,
    whiteSpace: 'nowrap',
  },
  editInput: {
    minWidth: 0,
    minHeight: 36,
    padding: '6px 10px',
    border: '1px solid var(--kea-color-primary)',
    borderRadius: 8,
    color: '#1e2248',
    fontSize: 13,
  },
  duplicateHint: { padding: '0 14px 8px', color: '#9a6a1f', fontSize: 11 },
  editHint: { padding: '0 14px 8px', color: '#a9455e', fontSize: 11 },
  hiddenZone: {
    borderTop: '1px solid #edf2f0',
    background: '#fbfefd',
  },
  hiddenTitle: {
    padding: '10px 14px 6px',
    color: '#66708a',
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
  },
  hiddenRow: {
    display: 'grid',
    minHeight: 44,
    gridTemplateColumns: 'minmax(0, 1fr) 32px',
    alignItems: 'center',
    gap: 8,
    padding: '6px 14px',
    borderTop: '1px solid #edf2f0',
    color: '#66708a',
    fontSize: 13,
  },
  hiddenText: {
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
});

const S = {
  ...themedStyles,
  wrapper: orderedCollectionStyles.wrapper,
  orderToolbar: {
    ...orderedCollectionStyles.toolbar,
  },
  resetOrderBtn: orderedCollectionStyles.resetButton,
  resetIcon: orderedCollectionStyles.resetIcon,
  zone: orderedCollectionStyles.zone,
  queryRow: {
    ...orderedCollectionStyles.row,
    gridTemplateColumns: '32px minmax(0, 1fr) auto 36px',
  },
  queryText: {
    ...orderedCollectionStyles.itemName,
    cursor: 'text',
    fontWeight: 500,
  },
  dragHandle: orderedCollectionStyles.dragHandle,
  dragIcon: orderedCollectionStyles.dragIcon,
  hideBtn: orderedCollectionStyles.actionButton,
  emptyState: orderedCollectionStyles.emptyState,
  addRow: orderedCollectionStyles.addRow,
  addInput: orderedCollectionStyles.addInput,
  addBtn: orderedCollectionStyles.addButton,
  hiddenZone: orderedCollectionStyles.hiddenZone,
  hiddenTitle: orderedCollectionStyles.hiddenTitle,
  hiddenRow: orderedCollectionStyles.hiddenRow,
  hiddenText: orderedCollectionStyles.hiddenText,
  itemStatus: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  automaticBadge: orderedCollectionStyles.automaticBadge,
};

const PinnedQueriesEditor = ({
  pinnedTerms,
  excludedTerms = [],
  generatedTerms,
  onChange,
  onExcludedTermsChange = () => {},
  autoFill = true,
  onAutoFillChange = () => {},
  hideHeader = false,
}) => {
  const t = useLocalizedMessage();
  const inputRef = useRef(null);
  const editInputRef = useRef(null);
  const [addInput, setAddInput] = useState('');
  const [duplicateHint, setDuplicateHint] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [editError, setEditError] = useState(false);
  const [hoveredTerm, setHoveredTerm] = useState(null);

  const excludedKeys = useMemo(
    () => new Set(excludedTerms.map(normalizeTerm)),
    [excludedTerms],
  );

  const effectiveItems = useMemo(() => {
    const artifactByKey = new Map();
    (generatedTerms || []).forEach((item) => {
      const key = normalizeTerm(item.term);
      if (key && !artifactByKey.has(key)) artifactByKey.set(key, item);
    });

    const seen = new Set();
    const manual = [];
    pinnedTerms.forEach((term) => {
      const key = normalizeTerm(term);
      if (!key || seen.has(key) || manual.length >= MAX_VISIBLE_QUERIES) return;
      const artifactItem = artifactByKey.get(key);
      manual.push({
        key,
        term: String(term).trim(),
        count: artifactItem ? artifactItem.count : null,
        manual: true,
      });
      seen.add(key);
    });

    const automatic = [];
    if (autoFill) (generatedTerms || []).forEach((item) => {
      const key = normalizeTerm(item.term);
      if (
        !key
        || seen.has(key)
        || excludedKeys.has(key)
        || manual.length + automatic.length >= MAX_VISIBLE_QUERIES
      ) return;
      automatic.push({
        key,
        term: String(item.term).trim(),
        count: item.count,
        manual: false,
      });
      seen.add(key);
    });

    return [...manual, ...automatic];
  }, [autoFill, excludedKeys, generatedTerms, pinnedTerms]);
  const { containerRef: listMotionRef, animateNextChange } = useOrderedCollectionMotion(
    effectiveItems.map(item => item.key),
  );

  useEffect(() => {
    if (editingIndex !== null && editInputRef.current) {
      editInputRef.current.focus();
      editInputRef.current.select();
    }
  }, [editingIndex]);

  const handleDragEnd = useCallback((result) => {
    if (!result.destination || result.source.index === result.destination.index) return;
    const moved = effectiveItems[result.source.index];
    if (!moved) return;
    if (moved.manual) {
      const nextPinned = [...pinnedTerms];
      const [term] = nextPinned.splice(result.source.index, 1);
      const destinationIndex = Math.min(result.destination.index, nextPinned.length);
      if (destinationIndex !== result.destination.index) {
        animateNextChange({ itemKey: moved.key, fromIndex: result.destination.index });
      }
      nextPinned.splice(destinationIndex, 0, term);
      onChange(nextPinned);
      return;
    }
    const nextPinned = [...pinnedTerms];
    const destinationIndex = Math.min(result.destination.index, nextPinned.length);
    if (destinationIndex !== result.destination.index) {
      animateNextChange({ itemKey: moved.key, fromIndex: result.destination.index });
    }
    nextPinned.splice(destinationIndex, 0, moved.term);
    onChange(nextPinned);
  }, [animateNextChange, effectiveItems, onChange, pinnedTerms]);

  const handleRemove = useCallback((item) => {
    const key = normalizeTerm(item.term);
    if (!key) return;

    const nextManual = pinnedTerms.filter((candidate) => normalizeTerm(candidate) !== key);
    if (item.manual) {
      onChange(nextManual);
      return;
    }
    if (!excludedKeys.has(key)) {
      onExcludedTermsChange([...excludedTerms, String(item.term).trim()]);
    }
  }, [excludedKeys, excludedTerms, onChange, onExcludedTermsChange, pinnedTerms]);

  const handlePin = useCallback((item) => {
    const key = normalizeTerm(item.term);
    animateNextChange();
    if (item.manual) {
      onChange(pinnedTerms.filter((candidate) => normalizeTerm(candidate) !== key));
      return;
    }
    onChange([...pinnedTerms, item.term]);
  }, [animateNextChange, onChange, pinnedTerms]);

  const handleRestore = useCallback((term) => {
    const key = normalizeTerm(term);
    onExcludedTermsChange(
      excludedTerms.filter((candidate) => normalizeTerm(candidate) !== key),
    );
  }, [excludedTerms, onExcludedTermsChange]);

  const commitAdd = useCallback(() => {
    const trimmed = addInput.trim();
    const key = normalizeTerm(trimmed);
    if (!key) {
      setAddInput('');
      return;
    }
    if (effectiveItems.some((item) => item.key === key)) {
      setDuplicateHint(true);
      setTimeout(() => setDuplicateHint(false), 2000);
      return;
    }

    if (excludedKeys.has(key)) handleRestore(trimmed);
    const next = pinnedTerms.length >= MAX_VISIBLE_QUERIES
      ? [...pinnedTerms.slice(0, MAX_VISIBLE_QUERIES - 1), trimmed]
      : [...pinnedTerms, trimmed];
    onChange(next);
    setAddInput('');
    setDuplicateHint(false);
  }, [addInput, effectiveItems, excludedKeys, handleRestore, onChange, pinnedTerms]);

  const handleAddKeyDown = useCallback((event) => {
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault();
      commitAdd();
    }
  }, [commitAdd]);

  const startEdit = useCallback((index) => {
    setEditingIndex(index);
    setEditValue(effectiveItems[index].term);
    setEditError(false);
  }, [effectiveItems]);

  const cancelEdit = useCallback(() => {
    setEditingIndex(null);
    setEditValue('');
    setEditError(false);
  }, []);

  const commitEdit = useCallback(() => {
    const trimmed = editValue.trim();
    const key = normalizeTerm(trimmed);
    if (!key) {
      setEditError(true);
      return;
    }
    if (trimmed === effectiveItems[editingIndex].term) {
      cancelEdit();
      return;
    }
    if (effectiveItems.some((item, index) => index !== editingIndex && item.key === key)) {
      setEditError(true);
      return;
    }

    const current = effectiveItems[editingIndex];
    if (current.manual) {
      const currentKey = normalizeTerm(current.term);
      onChange(pinnedTerms.map((term) => (
        normalizeTerm(term) === currentKey ? trimmed : term
      )));
    } else {
      onChange([...pinnedTerms, trimmed]);
      if (!excludedKeys.has(current.key)) {
        onExcludedTermsChange([...excludedTerms, current.term]);
      }
    }
    cancelEdit();
  }, [
    cancelEdit,
    editValue,
    editingIndex,
    effectiveItems,
    excludedKeys,
    excludedTerms,
    onChange,
    onExcludedTermsChange,
    pinnedTerms,
  ]);

  const handleEditKeyDown = useCallback((event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      commitEdit();
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      cancelEdit();
    }
  }, [cancelEdit, commitEdit]);

  return (
    <div style={S.wrapper}>
      {!hideHeader && (
        <div style={S.header}>
          <div>
            <div style={S.headerTitle}>{t('empty.query.editor.title')}</div>
            <div style={S.headerSub}>{t('empty.query.editor.subtitle')}</div>
          </div>
          <div style={S.headerMeta}>
            {t('empty.query.editor.meta.shown', { count: effectiveItems.length })}
            {' · '}
            {t('empty.query.collection.pinned.count', { count: pinnedTerms.length })}
          </div>
        </div>
      )}

      <div style={S.addRow}>
        <input
          ref={inputRef}
          type="text"
          value={addInput}
          onChange={(event) => {
            setAddInput(event.target.value);
            setDuplicateHint(false);
          }}
          onKeyDown={handleAddKeyDown}
          placeholder={t('empty.query.form.terms.add')}
          style={S.addInput}
        />
        <button type="button" onClick={commitAdd} style={S.addBtn}>
          {t('empty.query.editor.add')}
        </button>
      </div>
      {duplicateHint && (
        <div style={S.duplicateHint}>{t('empty.query.form.terms.duplicate')}</div>
      )}

      <div style={S.orderToolbar}>
        <span style={orderedCollectionStyles.toolbarSummary}>
          <span style={orderedCollectionStyles.toolbarStrong}>{pinnedTerms.length}</span>
          {' '}{t('empty.query.collection.pinned')}
          {' · '}{effectiveItems.length} {t('empty.query.collection.visible.of', { limit: MAX_VISIBLE_QUERIES })}
        </span>
        <span style={orderedCollectionStyles.toolbarActions}>
          <AutoFillControl
            enabled={autoFill}
            limit={MAX_VISIBLE_QUERIES}
            unit={t('empty.query.autofill.queries')}
            onChange={onAutoFillChange}
          />
        </span>
      </div>

      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="effective-queries">
          {(provided) => (
            <div
              ref={(node) => {
                provided.innerRef(node);
                listMotionRef.current = node;
              }}
              {...provided.droppableProps}
              style={S.zone}
            >
              {effectiveItems.length === 0 && (
                <div style={S.emptyState}>{t('empty.query.editor.generated.empty')}</div>
              )}
              {effectiveItems.map((item, index) => (
                <Draggable
                  key={`query-${item.key}`}
                  draggableId={`query-${item.key}`}
                  index={index}
                  isDragDisabled={editingIndex !== null}
                >
                  {(drag, snapshot) => (
                    <div
                      ref={drag.innerRef}
                      {...drag.draggableProps}
                      {...drag.dragHandleProps}
                      onMouseEnter={() => setHoveredTerm(item.key)}
                      onMouseLeave={() => setHoveredTerm(null)}
                      data-collection-row
                      data-collection-key={item.key}
                      style={{
                        ...S.queryRow,
                        ...drag.draggableProps.style,
                        ...(editingIndex === index ? {
                          gridTemplateColumns: '28px minmax(0, 1fr) 76px',
                        } : null),
                        ...(item.manual ? S.rowPinned : null),
                        ...(hoveredTerm === item.key ? { background: '#fbfefd' } : null),
                        ...(snapshot?.isDragging ? {
                          borderRadius: 8,
                          cursor: 'grabbing',
                          boxShadow: '0 8px 22px rgba(30, 34, 72, 0.14)',
                        } : null),
                      }}
                    >
                      {item.manual ? (
                        <span style={S.pinnedPosition}>{index + 1}</span>
                      ) : (
                        <div
                          style={S.dragHandle}
                          aria-label={t('empty.query.editor.drag', { term: item.term })}
                        >
                          <DragIndicatorIcon style={S.dragIcon} aria-hidden="true" />
                        </div>
                      )}

                      {editingIndex === index ? (
                        <>
                          <input
                            ref={editInputRef}
                            type="text"
                            value={editValue}
                            onChange={(event) => {
                              setEditValue(event.target.value);
                              setEditError(false);
                            }}
                            onKeyDown={handleEditKeyDown}
                            style={S.editInput}
                            aria-label={t('empty.query.editor.row.edit.label', { term: item.term })}
                          />
                          <RowEditActions
                            confirmLabel={t('empty.query.editor.row.confirm')}
                            cancelLabel={t('empty.query.editor.row.cancel')}
                            onConfirm={commitEdit}
                            onCancel={cancelEdit}
                          />
                        </>
                      ) : (
                        <>
                          <span
                            role="button"
                            tabIndex={0}
                            title={item.term}
                            style={S.queryText}
                            onClick={() => startEdit(index)}
                            onKeyDown={(event) => event.key === 'Enter' && startEdit(index)}
                            aria-label={t('empty.query.editor.row.edit.label', { term: item.term })}
                          >
                            {item.term}
                          </span>
                          <PinAction
                            pinned={item.manual}
                            label={t(
                              item.manual
                                ? 'empty.query.collection.unpin.item'
                                : 'empty.query.collection.pin.item',
                              { name: item.term },
                            )}
                            onClick={() => handlePin(item)}
                          />
                          <Tooltip title={t(
                            item.manual
                              ? 'empty.query.collection.remove.item'
                              : 'empty.query.collection.block.item',
                            { name: item.term },
                          )}>
                            <IconButton
                              size="small"
                              style={{
                                ...S.hideBtn,
                                opacity: hoveredTerm === item.key ? 1 : 0.55,
                              }}
                              aria-label={t(
                                item.manual
                                  ? 'empty.query.collection.remove.item'
                                  : 'empty.query.collection.block.item',
                                { name: item.term },
                              )}
                              onClick={() => handleRemove(item)}
                            >
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      {editError && (
        <div style={S.editHint}>{t('empty.query.editor.row.edit.error')}</div>
      )}

      {excludedTerms.length > 0 && (
        <div style={S.hiddenZone}>
          <div style={S.hiddenTitle}>{t('empty.query.collection.blocked')}</div>
          {excludedTerms.map((term) => (
            <div key={`hidden-${normalizeTerm(term)}`} style={S.hiddenRow}>
              <span title={term} style={S.hiddenText}>{term}</span>
              <Tooltip title={t('empty.query.editor.restore.label', { term })}>
                <IconButton
                  size="small"
                  style={S.hideBtn}
                  aria-label={t('empty.query.editor.restore.label', { term })}
                  onClick={() => handleRestore(term)}
                >
                  <RestoreIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PinnedQueriesEditor;
